import React from 'react';
import {
  Pressable,
  Text,
  StyleSheet,
  ActivityIndicator,
  PressableProps,
  StyleProp,
  ViewStyle,
  View,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Colors, Fonts, Radii, ControlHeight, Brand } from '../constants';

export type ButtonVariant =
  | 'primary'   // accent fill — the one main CTA per view
  | 'secondary' // linen fill, green text, hairline border
  | 'outline'   // transparent, green ink, sand border (sign-up beside log-in)
  | 'ghost'     // transparent, hairline border, green text (section header actions)
  | 'dark'      // Bosgroen fill
  | 'white'     // white fill, green ink — on linen panels
  | 'danger';   // destructive red fill

export type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends Omit<PressableProps, 'style' | 'onPress'> {
  title: string;
  onPress: () => void;
  loading?: boolean;
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Ionicon before the label */
  icon?: keyof typeof Ionicons.glyphMap;
  /** Ionicon after the label */
  iconRight?: keyof typeof Ionicons.glyphMap;
  style?: StyleProp<ViewStyle>;
}

interface Palette {
  bg: string;
  hover: string;
  fg: string;
  border: string;
}

const PALETTE: Record<ButtonVariant, Palette> = {
  primary:   { bg: Colors.primary, hover: Colors.primaryDark, fg: Colors.white, border: 'transparent' },
  secondary: { bg: Colors.background, hover: Colors.primaryLight, fg: Colors.textMedium, border: Colors.borderLight },
  outline:   { bg: 'transparent', hover: Colors.background, fg: Colors.textDark, border: Colors.border },
  ghost:     { bg: 'transparent', hover: Colors.background, fg: Colors.textMedium, border: Colors.borderLight },
  dark:      { bg: Brand.bosgroen, hover: '#2A4F42', fg: Colors.white, border: 'transparent' },
  white:     { bg: Colors.white, hover: Colors.primaryLight, fg: Colors.textDark, border: Colors.borderLight },
  danger:    { bg: Colors.error, hover: '#8C2F25', fg: Colors.white, border: 'transparent' },
};

/**
 * Button (design v2): square-shouldered controls (radius 6, all sizes), 48px tall (52 lg, 34 sm),
 * Poppins semibold 15 label (13 on sm), optional Ionicons on either side.
 * Hover changes the fill on web, press scales to .98, disabled fades to 50%.
 */
export const Button: React.FC<ButtonProps> = ({
  title,
  onPress,
  loading = false,
  variant = 'primary',
  size = 'md',
  icon,
  iconRight,
  disabled,
  style,
  ...props
}) => {
  const palette = PALETTE[variant] ?? PALETTE.primary;
  const off = !!disabled || loading;
  const sizeStyle = size === 'sm' ? styles.sm : size === 'lg' ? styles.lg : styles.md;
  const iconSize = size === 'sm' ? 15 : 18;

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityState={{ disabled: off, busy: loading }}
      onPress={onPress}
      disabled={off}
      style={({ pressed, hovered }: any) => [
        styles.base,
        sizeStyle,
        { backgroundColor: hovered && !off ? palette.hover : palette.bg, borderColor: palette.border },
        off && styles.disabled,
        pressed && !off && styles.pressed,
        style,
      ]}
      {...props}
    >
      {loading ? (
        <ActivityIndicator color={palette.fg} size="small" />
      ) : (
        <View style={styles.content}>
          {icon ? <Ionicons name={icon} size={iconSize} color={palette.fg} /> : null}
          <Text
            style={[styles.label, size === 'sm' && styles.labelSm, { color: palette.fg }]}
            numberOfLines={1}
          >
            {title}
          </Text>
          {iconRight ? <Ionicons name={iconRight} size={iconSize - 2} color={palette.fg} /> : null}
        </View>
      )}
    </Pressable>
  );
};

const styles = StyleSheet.create({
  base: {
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderRadius: Radii.control,
    ...(Platform.OS === 'web' && { cursor: 'pointer', transitionDuration: '150ms' } as any),
  },
  md: {
    height: ControlHeight.md,
    paddingHorizontal: 20,
  },
  lg: {
    height: ControlHeight.lg,
    paddingHorizontal: 20,
  },
  sm: {
    height: ControlHeight.sm,
    paddingHorizontal: 14,
  },
  content: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    maxWidth: '100%',
  },
  label: {
    fontFamily: Fonts.semiBold,
    fontSize: 15,
    lineHeight: 20,
    flexShrink: 1,
  },
  labelSm: {
    fontSize: 13,
    lineHeight: 16,
  },
  disabled: {
    opacity: 0.5,
    ...(Platform.OS === 'web' && { cursor: 'default' } as any),
  },
  pressed: {
    transform: [{ scale: 0.98 }],
  },
});
